<template>
  <div class="page-container">
    <div class="page-container-body" v-show="isFormReady">
      <div class="d-flex align-center ga-2 mb-8">
        <span @click="goBack()" class="goBack">
          <v-icon>
            mds:arrow-back
          </v-icon>
        </span>
        <h3 class="page-title">{{ isReplacing ? 'Replace Payment Method' : 'Add Payment Method' }}</h3>
      </div>

      <div class="plan-badge mb-4">
        At {{ brand.name }}, we prioritize your security and peace of mind. That’s why all credit card payments on our platform
        are processed securely through <strong>Stripe</strong>, a globally trusted payment provider.
        With <strong>Stripe’s industry-leading encryption and PCI DSS compliance</strong>, your payment details are
        handled with the highest level of security, ensuring they are never stored or exposed on our servers.
        Feel confident knowing your sensitive information is protected every step of the way.
      </div>

      <div class="manage-billing-form">
        <div v-if="isStripeLoading || !stripe" class="stripe-loading">
          <v-progress-circular
            indeterminate
            color="primary"
            size="32"
            width="2">
          </v-progress-circular>
          <span class="loading-text">Loading payment form...</span>
        </div>
        <div class="elements" v-show="!isStripeLoading && !!stripe">
          <div id="address-element" class="mb-3"></div>
          <div class="mb-3">
            <label class="Label" for="Field-countryInput">Card number</label>
            <div id="card-number-element" class="Input"></div>
          </div>
          <div class="d-flex justify-space-between ga-3 mb-3">
            <div class="w-50">
              <label class="Label" for="Field-countryInput">Expiration</label>
              <div id="card-expiry-element" class="Input"></div>
            </div>
            <div class="w-50">
              <label class="Label" for="Field-countryInput">CVC</label>
              <div id="card-cvc-element" class="Input"></div>
            </div>
          </div>

          <v-checkbox v-model="isBusiness" label="Purchase as a business or organization" hide-details class="mb-3" />

          <transition name="slide-fade">
            <div v-if="isBusiness" class="d-flex justify-space-between ga-3 mb-10">
              <div class="w-50">
                <v-autocomplete
                  v-model="taxIdType"
                  :items="taxIdTypes"
                  :rules="taxIdTypeRules"
                  item-title="description"
                  item-value="description"
                  label="Tax ID type"
                  return-object
                  variant="outlined"
                  density="compact"
                  :menu-props="{ maxWidth: 0 }"
                />
              </div>
              <div class="w-50">
                <v-text-field
                  v-model="taxId"
                  :placeholder="taxIdPlaceholder"
                  :rules="taxIdRules"
                  label="Tax ID number"
                  variant="outlined"
                  density="compact"
                />
              </div>
            </div>
          </transition>

          <v-btn
            rounded="lg"
            size="large"
            variant="flat"
            color="primary"
            :loading="loading"
            @click="updatePayment"
          >
            {{ isReplacing ? 'Replace Payment Method' : 'Add Payment Method' }}
          </v-btn>
        </div>
      </div>
    </div>
    <div class="page-loading-container-body" v-if="!isFormReady">
      <v-progress-circular
        indeterminate
        color="primary"
        size="32"
        width="2">
      </v-progress-circular>
      <span>{{ processing ? 'Saving your payment method...' : 'Loading payment method...' }}</span>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { loadStripe } from '@stripe/stripe-js'
import { isDarkTheme } from '@/composables/useAppTheme'
import store from '@/store'
import taxIdTypesData from '@/api/data/taxIdTypes.json'
import brand from '@/composables/useBrand'
import errorReporter from '@/utils/errorReporter'
import { hasEndedUnchosenTrial, hasUnchosenTrial } from '@/utils/payments'

export default {
  computed: {
    brand: () => brand,
    ...mapState({
      currentUser: state => state.user.user,
      activeSubscription: state => state.payments.activeSubscription,
      paymentMethod: state => state.payments.paymentMethod,
      loadingActiveSubscription: state => state.payments.loadingActiveSubscription,
      loadingPaymentMethod: state => state.payments.loadingPaymentMethod,
      subscriptionAccessType: state => state.payments.subscriptionAccessType,
      isSubscriptionAdmin: state => state.payments.isSubscriptionAdmin
    }),
    hasCardOnFile () {
      return !!this.paymentMethod?.card?.last4
    },
    isFormReady () {
      return !this.loadingPaymentMethod &&
        !this.processing &&
        !!this.activeSubscription &&
        !this.loadingActiveSubscription &&
        !hasUnchosenTrial(this.activeSubscription) &&
        !hasEndedUnchosenTrial(this.activeSubscription)
    },
    taxIdPlaceholder () {
      return this.taxIdType ? this.taxIdType.example : ''
    },
    taxIdRules () {
      if (!this.isBusiness || !this.taxIdType || this.disableValidation) return []
      const pattern = new RegExp(this.taxIdType.pattern)
      return [
        v => !!v || 'Tax ID is required',
        v => pattern.test(v) || 'Invalid Tax ID format'
      ]
    },
    taxIdTypeRules () {
      return this.isBusiness ? [v => !!v || 'Tax ID type is required'] : []
    },
    stripeAppearance () {
      if (isDarkTheme()) {
        return {
          theme: 'night',
          variables: {
            colorPrimary: '#5A9DF5',
            colorBackground: '#1E2228',
            colorText: '#E8EAF0',
            colorDanger: '#FF7777',
            colorTextPlaceholder: '#8892A0'
          }
        }
      }
      return { theme: 'stripe' }
    },
    stripeCardStyle () {
      if (isDarkTheme()) {
        return {
          base: {
            color: '#E8EAF0',
            backgroundColor: '#1E2228',
            iconColor: '#E8EAF0',
            '::placeholder': { color: '#8892A0' }
          },
          invalid: {
            color: '#FF7777',
            iconColor: '#FF7777'
          }
        }
      }
      return {
        base: {
          color: '#30313d',
          backgroundColor: '#ffffff',
          iconColor: '#30313d',
          '::placeholder': { color: '#757680' }
        },
        invalid: {
          color: '#df1b41',
          iconColor: '#df1b41'
        }
      }
    }
  },
  methods: {
    goBack () {
      this.$router.push('/subscription/billing')
    },
    initializeTaxIdFromPaymentMethod () {
      if (!this.paymentMethod?.taxId) {
        this.disableValidation = false
        return
      }

      const matchingType = this.taxIdTypes.find(
        taxType => taxType.country_code === this.paymentMethod.taxId.country
      )

      if (matchingType) {
        this.skipBusinessWatcher = true
        this.isBusiness = true
        this.taxIdType = matchingType
        this.taxId = this.paymentMethod.taxId.value

        this.$nextTick(() => {
          this.disableValidation = false
          this.skipBusinessWatcher = false
        })
      } else {
        this.disableValidation = false
      }
    },
    async configureStripe () {
      this.isStripeLoading = true
      try {
        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
        this.elements = this.stripe.elements({ appearance: this.stripeAppearance })

        const addressDefaultValues = {
          name: this.paymentMethod?.billingDetails?.name || `${this.currentUser?.firstname} ${this.currentUser?.lastname}`
        }

        if (this.paymentMethod?.billingDetails?.address) {
          const addr = this.paymentMethod.billingDetails.address
          addressDefaultValues.address = {}
          if (addr.line1) addressDefaultValues.address.line1 = addr.line1
          if (addr.line2) addressDefaultValues.address.line2 = addr.line2
          if (addr.city) addressDefaultValues.address.city = addr.city
          if (addr.state) addressDefaultValues.address.state = addr.state
          if (addr.country) addressDefaultValues.address.country = addr.country
          if (addr.postalCode) addressDefaultValues.address.postal_code = addr.postalCode
        }

        // Address Element
        this.addressElement = this.elements.create('address', {
          mode: 'billing',
          autocomplete: { mode: 'google_maps_api', apiKey: 'AIzaSyDkyS96K5XC-Yt6dHHzU5KilI2xw4Nc5do' },
          defaultValues: addressDefaultValues
        })
        this.addressElement.mount('#address-element')
        this.addressElement.on('change', this.handleAddressChange)
        // CardNumber Element
        this.cardNumberElement = this.elements.create('cardNumber', { style: this.stripeCardStyle })
        this.cardNumberElement.mount('#card-number-element')
        // CardExpiry Element
        this.cardExpiryElement = this.elements.create('cardExpiry', { style: this.stripeCardStyle })
        this.cardExpiryElement.mount('#card-expiry-element')
        // CardCvc Element
        this.cardCvcElement = this.elements.create('cardCvc', { style: this.stripeCardStyle })
        this.cardCvcElement.mount('#card-cvc-element')

        this.isStripeLoading = false
      } catch (err) {
        console.log(err)
        this.isStripeLoading = false
        this.$router.push({ path: '/plans' })
      }
    },
    async updatePayment () {
      this.loading = true
      try {
        await this.submitPaymentMethod()
      } catch (err) {
        console.error(err)
        errorReporter.reportError({
          message: err?.message || String(err),
          stack: err?.stack || '',
          type: 'payment-error'
        })
        store.commit('snackbar/showMessage', {
          content: 'Something went wrong, please try again.',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      } finally {
        this.loading = false
        this.processing = false
      }
    },
    async submitPaymentMethod () {
      const address = await this.addressElement.getValue()
      if (!address.complete) {
        store.commit('snackbar/showMessage', {
          content: 'Your billing address is incomplete.',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        return
      }

      if (
        this.cardNumberElement?._invalid ||
        this.cardExpiryElement?._invalid ||
        this.cardCvcElement?._invalid ||
        (this.isBusiness && !this.taxIdType)
      ) {
        let message = ''

        if (this.cardNumberElement?._invalid) {
          message = 'Invalid card number'
        } else if (this.cardExpiryElement?._invalid) {
          message = 'Invalid card expiry date'
        } else if (this.cardCvcElement?._invalid) {
          message = 'Invalid card CVC'
        } else if (!this.taxIdType) {
          message = 'Invalid Tax ID type'
        } else {
          message = 'Something went wrong'
        }

        store.commit('snackbar/showMessage', {
          content: message,
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        return
      }

      if (this.isBusiness) {
        const pattern = new RegExp(this.taxIdType.pattern)
        if (!this.taxId || !pattern.test(this.taxId)) {
          store.commit('snackbar/showMessage', {
            content: 'Invalid Tax ID',
            timeout: 6000,
            color: 'red',
            location: 'center center'
          }, { root: true })
          return
        }
      }

      const { error, paymentMethod } = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.cardNumberElement,
        billing_details: {
          email: this.currentUser?.email,
          name: `${this.currentUser?.firstname} ${this.currentUser?.lastname}`,
          address: {
            city: address.value.address.city,
            country: address.value.address.country,
            line1: address.value.address.line1,
            state: address.value.address.state,
            postal_code: address.value.address.postal_code,
            ...(!!address.value.address.line2 && { line2: address.value.address.line2 })
          }
        }
      })

      if (error) {
        console.error(error)
        return store.commit('snackbar/showMessage', {
          content: error.message,
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }

      this.processing = true

      const addPaymentMethodParams = {
        paymentMethodId: paymentMethod.id,
        newCustomerInfo: {
          name: `${this.currentUser?.firstname} ${this.currentUser?.lastname}`,
          email: this.currentUser?.email,
          ...(this.isBusiness && {
            taxIdType: this.taxIdType.type,
            taxId: this.taxId
          })
        }
      }

      const addPaymentMethodResponse = await store.dispatch('payments/addPaymentMethod', addPaymentMethodParams)
      if (addPaymentMethodResponse?.success) {
        const wasReplacing = this.hasCardOnFile
        await store.dispatch('payments/getPaymentMethod')
        await store.dispatch('payments/getActiveSubscription')

        if (this.isSubscriptionAdmin && this.subscriptionAccessType === 'LIMITED' && !!this.activeSubscription?.lastSubscriptionDetails) {
          // Renewing reuses the last price, which for a trial that ended unchosen would silently buy Pro.
          if (!wasReplacing) {
            store.commit('snackbar/showMessage', { content: 'Your card is saved. Now choose your plan to continue.' }, { root: true })
            this.$router.push('/plans')
            return
          }

          const renewResponse = await store.dispatch('payments/renewSubscription', { stripe: this.stripe })

          if (renewResponse?.success) {
            store.commit('snackbar/showMessage', { content: 'Your subscription is active again!' }, { root: true })
            this.$router.push('/dashboard')
          } else {
            store.commit('snackbar/showMessage', {
              content: renewResponse?.message || 'Your card was saved, but we could not renew your subscription. Please use Renew Plan from My Plan.',
              timeout: 6000,
              color: 'red',
              location: 'center center'
            }, { root: true })
          }
          return
        }

        this.goBack()
        this.$store.commit('snackbar/showMessage', { content: wasReplacing ? 'Payment method updated successfully!' : 'Payment method added successfully!' }, { root: true })
      } else {
        return store.commit('snackbar/showMessage', {
          content: addPaymentMethodResponse?.message || 'Error, try again!',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }
    },
    handleAddressChange (event) {
      if (event.value) {
        this.currentAddress = event.value
      }
      if (this.isBusiness) {
        this.prefillTaxIdType()
      }
    },
    prefillTaxIdType () {
      if (!this.currentAddress?.address?.country) {
        return
      }

      const countryCode = this.currentAddress.address.country

      const foundTaxType = this.taxIdTypes.find(taxType => taxType.country_code === countryCode)

      if (foundTaxType) {
        this.taxIdType = foundTaxType
      }
    }
  },
  data () {
    return {
      loading: false,
      processing: false,
      isReplacing: false,
      stripe: null,
      elements: null,
      isStripeLoading: false,
      addressElement: null,
      cardNumberElement: null,
      cardExpiryElement: null,
      cardCvcElement: null,
      isBusiness: false,
      taxId: '',
      taxIdType: null,
      currentAddress: null,
      taxIdTypes: taxIdTypesData,
      disableValidation: true,
      skipBusinessWatcher: false
    }
  },
  async mounted () {
    if (this.currentUser) {
      await store.dispatch('payments/getPaymentMethod')
      this.isReplacing = this.hasCardOnFile
      this.initializeTaxIdFromPaymentMethod()

      if (!this.stripe) {
        await this.configureStripe()
      }
    }
  },
  watch: {
    isSubscriptionAdmin: function (val) {
      if (val === false) {
        this.$router.push('/subscription/my-plan')
      }
    },
    activeSubscription: {
      handler (activeSubscription) {
        if (hasUnchosenTrial(activeSubscription) || hasEndedUnchosenTrial(activeSubscription)) {
          this.$router.replace('/plans')
        }
      },
      immediate: true
    },
    currentUser: async function (user) {
      if (user && !this.stripe) {
        await store.dispatch('payments/getPaymentMethod')
        this.isReplacing = this.hasCardOnFile
        this.initializeTaxIdFromPaymentMethod()
        await this.configureStripe()
      }
    },
    taxIdType (newValue, oldValue) {
      if (newValue !== oldValue && !this.skipBusinessWatcher) {
        this.taxId = ''
      }
    },
    isBusiness (isNowBusiness) {
      if (isNowBusiness && !this.skipBusinessWatcher) {
        this.prefillTaxIdType()
      }
    },
    stripeAppearance (newAppearance) {
      if (this.elements) {
        this.elements.update({ appearance: newAppearance })
      }
    },
    stripeCardStyle (newStyle) {
      this.cardNumberElement?.update({ style: newStyle })
      this.cardExpiryElement?.update({ style: newStyle })
      this.cardCvcElement?.update({ style: newStyle })
    }
  }
}
</script>

<style scoped>
.page-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-container-body {
  flex-grow: 1;
  padding: 0 4px;
  overflow: auto;
}

.page-container-body::-webkit-scrollbar {
  width: 6px;
}

.page-container-body::-webkit-scrollbar-track {
  background: transparent;
}

.page-container-body::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.page-container-body::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.page-loading-container-body {
  gap: 16px;
  height: 100%;
  display: flex;
  font-size: 16px;
  font-weight: 400;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  color: var(--dark-4, #363A40);
}

.goBack {
  cursor: pointer;
  color: var(--dark-4, #363A40);
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.billing-info {
  gap: 16px;
  display: flex;
  margin-bottom: 32px;
  flex-direction: column;
}

.info-row {
  gap: 8px;
  display: flex;
  align-items: center;
  color: var(--dark-3, #61676E);
}

.info-row-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 16.8px;
  color: var(--dark-3, #61676E);
}

.viewBillingHistory {
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;
  color: var(--blue-4, #0D66D0);
}

.plan-badge {
  gap: 8px;
  padding: 8px;
  font-size: 12px;
  font-weight: 400;
  border-radius: 8px;
  align-items: center;
  color: var(--dark-4, #363A40);
  background: var(--light-2, #F5F2F0);
}

.stripe-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 20px;
}

.loading-text {
  font-size: 14px;
  color: var(--dark-3, #61676E);
}

.elements {
  --accessibleColorOnColorPrimary: #fff;
  --p-colorPrimaryHover: #0b81fa;
  --p-colorPrimaryActive: #228cfa;
  --p-colorPrimaryDisabled: #d9d9d9;
  --p-colorPrimaryDisabledText: #8d8d8d;
  --p-colorBackgroundDisabled: #f2f2f2;
  --p-colorBackgroundDisabledDeemphasize05: #e6e6e6;
  --p-colorBackgroundDeemphasize10: #e6e6e6;
  --colorTextPlaceholder: #757680;
  --fontWeightMedium: 600;
  --colorPrimary: #0570de;
  --colorBackground: #fff;
  --colorText: #30313d;
  --borderRadius: 5px;
  --colorPrimaryText: var(--accessibleColorOnColorPrimary);
  --fontSizeSm: 14.88px;
  --p-spacingXs: 1px;
  --p-spacingSm: 2px;
  --p-spacing1: 4px;
  --p-spacing2: 6px;
  --p-spacing3: 12px;
  --fontFamily: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;

  .Input {
    padding: var(--p-spacing3);
    background-color: var(--colorBackground);
    border-radius: var(--borderRadius);
    transition: background 0.15s ease, border 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;
    border: 1px solid var(--p-colorBackgroundDeemphasize10);
    box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 6px rgba(0, 0, 0, 0.02);
  }

  .Input::placeholder {
    color: var(--colorTextPlaceholder);
  }

  .Input:disabled {
    background-color: var(--p-colorBackgroundDisabled);
    color: var(--colorTextPlaceholder);
    border-color: var(--p-colorBackgroundDisabledDeemphasize05);
    box-shadow: none;
  }

  .Label {
    display: block;
    touch-action: manipulation;
    margin-bottom: var(--p-spacing1);
    font-size: var(--fontSizeSm);
    transition: transform 0.5s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s cubic-bezier(0.19, 1, 0.22, 1);
    font-family: var(--fontFamily);
  }

  .Button {
    padding: var(--p-spacing3);
    background-color: var(--colorPrimary);
    font-weight: var(--fontWeightMedium);
    color: var(--colorPrimaryText);
    border-radius: var(--borderRadius);
    transition: background 0.15s ease, color 0.15s ease, border 0.15s ease, box-shadow .15s ease;
  }

  .Button:hover {
    background-color: var(--p-colorPrimaryHover);
  }

  .Button:active {
    background-color: var(--p-colorPrimaryActive);
  }

  .Button:disabled {
    background-color: var(--p-colorPrimaryDisabled);
    color: var(--p-colorPrimaryDisabledText);
  }
}

[data-theme="dark"] .elements {
  --colorBackground: #1E2228;
  --colorText: #E8EAF0;
  --colorTextPlaceholder: #8892A0;
  --p-colorBackgroundDeemphasize10: #3A424D;
  --p-colorBackgroundDisabled: #2E343C;
  --p-colorBackgroundDisabledDeemphasize05: #3A424D;

  .Input {
    box-shadow: none;
  }
}
</style>
